<script setup lang="ts">
import { useMusicSettings } from '@/composables/theme/theme'
import { isInWechat, isNotEmpty } from '@/utils/util'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { debounce } from 'perfect-debounce'
import { showGiftPlay } from '@/composables/memorize/gift'
import { audioManager, generateAudioId, type Subscriber } from '@/composables/music/musicmanager'
import { isIOS } from '@vueuse/core'

// true 是 light，false 是dark
const props = defineProps({
  light: {
    type: Boolean,
    default: true
  },
  audioUrls: {
    type: Array as () => string[],
    default: (): string[] => []
  },
  serviceId: {
    type: String,
    default: ''
  },
  inSetting: {
    type: Boolean,
    default: false
  },
  isPreview: {
    type: Boolean,
    default: false
  }
})

// 新增环境检测方法
const isIOSWechat = () => {
  return isIOS && isInWechat()
}

const musicSettings = useMusicSettings()

const componentId = generateAudioId(`music-toggle-${props.serviceId}`)
const subscriber: Subscriber = {
  id: componentId,
  priority: props.isPreview ? 1 : 0, // 主题音乐设为0级
  play: async () => {
    await playAudio()
    return Promise.resolve()
  },
  pause: () => {
    if (audio.value && !audio.value.paused) {
      enabled.value = false
      audio.value.pause()
    }
  },
  onStopped: (callback) => {
    if (audio.value) {
      audio.value.addEventListener('ended', callback)
    }
  }
}

const light = computed(() => {
  return props.light
})

const enabled = ref(false)

onMounted(() => {
  // iOS微信环境延迟初始化audio
  if (!isIOSWechat()) {
    audio.value = new Audio()
    audio.value.preload = 'metadata'
  }

  audioManager.subscribe(subscriber)
  initAudioState()
})

// 新增初始化方法
function initAudioState() {
  const enable = musicSettings.value[props.serviceId]
  enabled.value = !!enable
  if (isNotEmpty(props.audioUrls) && enable) {
    playAudio()
  }
}

watch(
  () => props.inSetting,
  (newValue) => {
    console.log('newValue:', newValue)
    if (!newValue) {
      const enable = musicSettings.value[props.serviceId]
      enabled.value = enable
      if (enable) {
        playAudio()
      } else {
        if (audio.value) {
          audio.value.pause()
        }
      }
    }
  },
  { deep: true }
)

const audioUrls = computed(() => {
  return props.audioUrls
})

const currentIndex = ref(0)

// 获取音频元素
const audio = ref<HTMLAudioElement | null>(null)

// 在组件内添加 abortController 引用
const abortController = ref<AbortController | null>(null)

// 监听 audioUrls 的变化
watch(
  () => props.audioUrls,
  debounce((newValue) => {
    if (audio.value) {
      audio.value.pause()
      audio.value.currentTime = 0
      currentIndex.value = 0 // 重置播放索引
      if (isNotEmpty(newValue) && (props.isPreview || enabled.value)) {
        playAudio()
      }
    }
  }, 300)
)

// 监听 enabled 的变化
watch(
  () => enabled.value,
  (newValue) => {
    if (newValue) {
      playAudio()
    } else {
      if (audio.value) {
        audio.value.pause()
      }
    }
  }
)

const emit = defineEmits(['openSetting'])

let retryCount = 0

// 重构播放逻辑
async function playAudio() {
  try {
    // iOS微信特殊处理
    if (isIOSWechat() && !audio.value) {
      audio.value = document.createElement('audio') as HTMLAudioElement
      audio.value.controls = false
      audio.value.style.display = 'none'
      document.body.appendChild(audio.value as unknown as Node)
    }

    if (!audio.value) return

    // 清理旧状态
    audio.value.pause()
    audio.value.currentTime = 0
    audio.value.onended = null

    // 设置新音源
    audio.value.src = props.audioUrls[currentIndex.value]

    // iOS预加载处理
    if (isIOSWechat()) {
      audio.value.muted = false
      audio.value.volume = 1
      await new Promise((resolve) => {
        audio.value?.addEventListener('loadedmetadata', resolve, { once: true })
      })
      // 触发微信播放权限
      try {
        await audio.value.play()
        await audio.value.pause()
      } catch {}
    }

    // 开始播放
    abortController.value?.abort()
    const controller = new AbortController()
    abortController.value = controller

    audioManager.pauseAll(componentId)
    await audio.value.play()

    retryCount = 0

    // 处理播放结束
    audio.value.addEventListener(
      'ended',
      () => {
        if (!controller.signal.aborted) {
          currentIndex.value = (currentIndex.value + 1) % props.audioUrls.length
          playAudio()
        }
      },
      { signal: controller.signal }
    )
  } catch (error: any) {
    if (error.name !== 'AbortError') {
      console.error('播放失败:', error)
      if (error.name === 'NotAllowedError') {
        // 等待用户交互后再次尝试播放
        const interactionEvents = ['click', 'touchstart']
        const onInteract = () => {
          interactionEvents.forEach((e) => document.removeEventListener(e, onInteract))
          if (enabled.value) {
            playAudio()
          }
        }
        interactionEvents.forEach((e) => document.addEventListener(e, onInteract, { once: true }))
      } else if (isIOSWechat() && retryCount++ < 3) {
        setTimeout(playAudio, 500)
      }
    }
  }
}

// 新增暂停方法
function pauseAudio() {
  enabled.value = false
  if (!props.isPreview) {
    delete musicSettings.value[props.serviceId]
  }
  audio.value?.pause()
}

// 修改后的toggle方法
function toggle() {
  // 在toggle函数中（242行）
  if (isIOSWechat() && !audio.value) {
    audio.value = document.createElement('audio') as HTMLAudioElement
    audio.value.controls = false
    audio.value.style.display = 'none'
    document.body.appendChild(audio.value as unknown as Node)
    audio.value.src = props.audioUrls[currentIndex.value]
  }

  if (audio.value?.paused) {
    enabled.value = true
    if (!props.isPreview) {
      musicSettings.value[props.serviceId] = true
    }
    playAudio()
  } else {
    pauseAudio()
  }
}

let isPlayBeforeGiftPlay: boolean | null = null

watch(
  () => showGiftPlay.value,
  (newVal) => {
    if (newVal) {
      // 判断audio正在播放，如果正在播放则记录为true
      if (audio.value && !audio.value.paused) {
        isPlayBeforeGiftPlay = true
        if (audio.value) {
          audio.value.pause()
        }
      } else {
        isPlayBeforeGiftPlay = false
      }
    } else {
      if (isPlayBeforeGiftPlay) {
        playAudio()
      }
      isPlayBeforeGiftPlay = null
    }
  }
)

// 修改卸载处理
onUnmounted(() => {
  if (abortController.value) {
    abortController.value.abort()
  }
  if (audio.value) {
    audio.value.pause()
    audio.value.currentTime = 0
    audio.value.src = ''
    audio.value.load() // 强制清空缓冲区
  }
  audioManager.unsubscribe(componentId)
})
</script>
<template>
  <div
    h-10
    w-10
    px-2
    cursor-pointer
    flex="~ row"
    items-center
    justify-center
    :class="[light ? 'hover:bg-gray-100/60 text-second' : 'hover:bg-gray-400/55 text-inverted', 'bg-transparent']"
    hover:backdrop-blur-sm
    rounded-full
    @click.stop.prevent="toggle">
    <div
      :class="[
        light ? 'text-theme-dark' : 'text-theme-inverted',
        enabled ? 'i-mingcute:music-2-fill' : 'i-fluent:music-note-off-1-24-filled',
        enabled ? 'breathe-animation' : ''
      ]"
      class="text-2xl inline-block"></div>
    <!-- <audio ref="audio" :src="audioUrls[currentIndex]" preload="auto" @play.capture="abortController?.abort()"></audio> -->
  </div>
</template>

<style scoped>
/* 定义复杂的呼吸感动画 */
@keyframes breathe {
  0% {
    transform: translateY(0) translateX(0) rotate(0deg);
  }
  100% {
    transform: translateY(0) translateX(0) rotate(360deg);
  }
  22% {
    transform: translateY(-5px) translateX(-2px) rotate(5deg);
  }
  39% {
    transform: translateY(1px) translateX(-4px) rotate(-5deg);
  }
  63% {
    transform: translateY(5px) translateX(1px) rotate(5deg);
  }
  80% {
    transform: translateY(-1px) translateX(4px) rotate(-5deg);
  }
}

/* 应用动画的类 */
.breathe-animation {
  animation: breathe 10s infinite;
}
</style>
